import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowUp, RotateCcw, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ClassicLoader from '@/components/ui/loader';
import { ApiRequestError, apiPost } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useAiStatus } from './ai';

type Turn = { role: 'user' | 'assistant'; content: string };

const STARTERS = [
  'What should I focus on today?',
  'How do I connect WhatsApp to the inbox?',
  'How do I send an invoice with VAT?',
  'How do I build a sequence?',
];

/**
 * "Ask Dovo": the assistant that slides in from the top bar, the way HubSpot's
 * assistant does. It answers how-to questions and reads a small, role-aware
 * snapshot of the person's pipeline; it never changes records itself.
 */
export function AssistantPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const { pathname } = useLocation();
  const { data: status } = useAiStatus();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const scroller = useRef<HTMLDivElement>(null);

  const ask = useMutation({
    mutationFn: (messages: Turn[]) => apiPost<{ reply: string }>('/ai/ask', { messages, page: pathname }),
    onSuccess: (result) => setTurns((current) => [...current, { role: 'assistant', content: result.reply }]),
    onError: (error) =>
      setTurns((current) => [
        ...current,
        {
          role: 'assistant',
          content: error instanceof ApiRequestError ? error.message : 'Sorry, I could not answer that just now.',
        },
      ]),
  });

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [turns, ask.isPending]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const send = (text: string) => {
    const question = text.trim();
    if (!question || ask.isPending) return;
    // Only the last few turns go up: enough context, bounded cost.
    const next = [...turns, { role: 'user' as const, content: question }];
    setTurns(next);
    setDraft('');
    ask.mutate(next.slice(-12));
  };

  return (
    <aside
      className={cn(
        'fixed inset-y-0 end-0 z-[55] flex w-full max-w-[26rem] flex-col border-s bg-card shadow-brand-lg transition-transform duration-200',
        open ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full',
      )}
      aria-hidden={!open}
      aria-label="Ask Dovo"
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-aurora text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="font-bold">{t('Ask Dovo')}</span>
        {status?.enabled && (
          <span className="ms-1 text-[11px] text-muted-foreground">
            {status.remaining}/{status.limit} {t('left today')}
          </span>
        )}
        <div className="ms-auto flex items-center gap-1">
          {turns.length > 0 && (
            <button
              type="button"
              onClick={() => setTurns([])}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              title={t('New chat')}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div ref={scroller} className="scrollbar-thin flex-1 space-y-3 overflow-y-auto p-4">
        {status && !status.enabled ? (
          <div className="rounded-lg border border-dashed p-5 text-center text-sm text-muted-foreground">
            {t('The AI assistant is not switched on for this workspace yet.')}
          </div>
        ) : turns.length === 0 ? (
          <div className="pt-6">
            <p className="text-lg font-bold">{t('Hi! How can I help?')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('Ask about your pipeline or how to do something in DovoCRM.')}
            </p>
            <div className="mt-5 space-y-2">
              {STARTERS.map((starter) => (
                <button
                  key={starter}
                  type="button"
                  onClick={() => send(t(starter))}
                  className="block w-full rounded-lg border px-3 py-2.5 text-start text-sm transition-colors hover:border-primary/50 hover:bg-primary/[0.04]"
                >
                  {t(starter)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          turns.map((turn, index) => (
            <div key={index} className={cn('flex', turn.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
                  turn.role === 'user' ? 'rounded-ee-sm bg-slate-800 text-white' : 'rounded-es-sm bg-muted',
                )}
              >
                {turn.content}
              </div>
            </div>
          ))
        )}
        {ask.isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ClassicLoader size={16} className="border-2" /> {t('Thinking…')}
          </div>
        )}
      </div>

      <form
        className="border-t p-3"
        onSubmit={(event) => {
          event.preventDefault();
          send(draft);
        }}
      >
        <div className="flex items-end gap-2 rounded-xl border bg-background p-2 focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(draft);
              }
            }}
            rows={1}
            placeholder={t('Ask anything…')}
            disabled={status ? !status.enabled : false}
            className="max-h-32 min-h-[2rem] flex-1 resize-none bg-transparent px-1 py-1 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Button type="submit" size="icon" className="h-8 w-8 rounded-lg" disabled={!draft.trim() || ask.isPending}>
            <ArrowUp />
          </Button>
        </div>
      </form>
    </aside>
  );
}
